import { create } from 'zustand'
import { apiClient } from '../lib/api'
import { track } from '../lib/analytics'
import type { Highlight } from '../lib/types'

interface HighlightState {
  highlights: Highlight[]
  loading: boolean
  error: string | null

  fetchHighlights: (threadId: number) => Promise<void>
  createHighlight: (threadId: number, data: {
    exchange_id: number
    selected_text?: string
    note?: string
  }) => Promise<Highlight | null>
  updateHighlight: (threadId: number, highlightId: number, data: {
    note?: string
    color?: string
  }) => Promise<void>
  deleteHighlight: (threadId: number, highlightId: number) => Promise<void>
  clearHighlights: () => void
}

export const useHighlightStore = create<HighlightState>((set, get) => ({
  highlights: [],
  loading: false,
  error: null,

  fetchHighlights: async (threadId) => {
    set({ loading: true, error: null })
    try {
      const data = await apiClient.get<Highlight[]>(
        `research/threads/${threadId}/highlights/`
      )
      set({ highlights: data })
    } catch {
      set({ error: 'Failed to load highlights.' })
    } finally {
      set({ loading: false })
    }
  },

  createHighlight: async (threadId, data) => {
    try {
      const highlight = await apiClient.post<Highlight>(
        `research/threads/${threadId}/highlights/`,
        data
      )
      set((s) => ({ highlights: [highlight, ...s.highlights] }))
      track('highlight_created', { thread_id: threadId, has_selected_text: !!data.selected_text })
      return highlight
    } catch {
      set({ error: 'Failed to save highlight.' })
      return null
    }
  },

  updateHighlight: async (threadId, highlightId, data) => {
    const prev = get().highlights
    set((s) => ({
      highlights: s.highlights.map((h) =>
        h.id === highlightId ? { ...h, ...data } : h
      ),
    }))
    try {
      await apiClient.patch(
        `research/threads/${threadId}/highlights/${highlightId}/`,
        data
      )
    } catch {
      set({ highlights: prev })
    }
  },

  deleteHighlight: async (threadId, highlightId) => {
    const prev = get().highlights
    set((s) => ({
      highlights: s.highlights.filter((h) => h.id !== highlightId),
    }))
    try {
      await apiClient.delete(
        `research/threads/${threadId}/highlights/${highlightId}/`
      )
    } catch {
      set({ highlights: prev })
    }
  },

  clearHighlights: () => set({ highlights: [], loading: false, error: null }),
}))
